import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { Box, Popover, styled, useTheme } from '@mui/material';
import { MouseEvent, useCallback, useEffect, useState } from 'react';

import {
  BaseDropdown,
  baseDropdownClasses,
  BaseDropdownLabel,
  BaseDropdownTrigger,
  DropdownCheckbox,
  DropdownTypography,
} from '#entities';

import { useIsIdle } from '../../data-context';
import { useRegionToggle } from '../../Layout';

import type { EWidgetRegion } from '#shared';

const StyledPopover = styled(Popover)({
  background: 'transparent',
  boxShadow: 'none',
  '& .MuiPaper-root': {
    animation: 'fadeInScale 300ms ease-out',
    transformOrigin: 'top right',
  },
});

const LayoutOptionsBox = styled(Box)(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: spacing(2),
  paddingRight: spacing(2),
  marginTop: spacing(2),
}));

interface LayoutDropdownProps {
  onOpenChange?: (open: boolean) => void;
}

export const LayoutDropdown = ({ onOpenChange }: LayoutDropdownProps) => {
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { enabledRegions, toggleRegion, layoutOptions, getOptionState } =
    useRegionToggle();

  const isIdle = useIsIdle();
  const open = Boolean(anchorEl);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      onOpenChange?.(true);
    },
    [onOpenChange],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleToggle = useCallback(
    (option: EWidgetRegion) => {
      toggleRegion(option);
      handleClose();
    },
    [toggleRegion, handleClose],
  );

  useEffect(() => {
    if (isIdle && open) {
      setTimeout(handleClose, 0);
    }
  }, [isIdle, open, handleClose]);

  return (
    <>
      <BaseDropdownTrigger
        className={baseDropdownClasses.button}
        onClick={handleClick}
      >
        <ViewColumnIcon
          sx={{ color: palette.common.white }}
          className={baseDropdownClasses.icon}
        />
      </BaseDropdownTrigger>

      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={0}
      >
        <Box width={180}>
          <BaseDropdown className={baseDropdownClasses.root}>
            <Box className={baseDropdownClasses.section}>
              <DropdownTypography pl={0.75} gutterBottom>
                Screen layout
              </DropdownTypography>

              <LayoutOptionsBox>
                {layoutOptions.map(option => {
                  const { disabled, tooltip } = getOptionState(option);

                  return (
                    <Box
                      key={option}
                      component="span"
                      title={tooltip}
                      sx={{ display: 'flex' }}
                    >
                      <BaseDropdownLabel
                        control={
                          <span style={{ display: 'inline-flex' }}>
                            <DropdownCheckbox
                              checked={enabledRegions.includes(option)}
                              onChange={() => handleToggle(option)}
                              disabled={disabled}
                            />
                          </span>
                        }
                        label={option[0].toUpperCase() + option.slice(1)}
                        className={baseDropdownClasses.label}
                      />
                    </Box>
                  );
                })}
              </LayoutOptionsBox>
            </Box>
          </BaseDropdown>
        </Box>
      </StyledPopover>
    </>
  );
};
